/**
 * Páginas restantes del panel: codificación de abiertas, usuarios, exportación
 * y bitácora de auditoría.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, downloadFile } from '../api/client';
import { useAdmin } from '../store';
import { BarChart, StatusChip, Tile, fmtInt, fmtPct } from '../components/charts';

/* ========================================================================== */
/*                        Codificación de abiertas (§9.5)                     */
/* ========================================================================== */

/**
 * Codificación de preguntas abiertas.
 *
 * La vista agrupa respuestas por texto normalizado: en C2 llegan por decenas
 * «falta de agua», «el agua», «no hay agua», y todas deben recibir el mismo
 * código. Codificar grupos en lugar de respuestas una por una es lo que hace
 * viable el plazo de 3 días que fija §9.5.
 *
 * El texto literal nunca se sobrescribe: vive en la respuesta y la codificación
 * se guarda aparte, para poder recodificar cuando el libro se ajusta tras el
 * piloto (§8).
 */
export function CodingPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('C2');
  const [selectedCode, setSelectedCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const progress = useQuery({ queryKey: ['coding', 'progress'], queryFn: api.codingProgress });
  const pending = useQuery({
    queryKey: ['coding', 'pending', question],
    queryFn: () => api.codingPending(question),
  });
  const codebooks = useQuery({ queryKey: ['codebooks'], queryFn: api.codebooks });
  const activeBook = codebooks.data?.find((book) => book.questions.includes(question));

  const codeGroup = useMutation({
    mutationFn: ({ answerIds, code }: { answerIds: string[]; code: string }) =>
      api.codeGroup(answerIds, code, activeBook?.id ?? null),
    onSuccess: (result) => {
      setMessage(`Se codificaron ${fmtInt(result.coded)} respuestas.`);
      void queryClient.invalidateQueries({ queryKey: ['coding'] });
    },
  });

  const autoCode = useMutation({
    mutationFn: () => api.autoCode(question),
    onSuccess: (result) => {
      setMessage(
        `Precodificadas ${fmtInt(result.autoCoded)} de ${fmtInt(result.pending)} pendientes; quedan ${fmtInt(result.remaining)}. ${result.note}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['coding'] });
    },
    onError: (e) =>
      setMessage(e instanceof ApiError ? e.message : 'No se pudo precodificar.'),
  });

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Codificación de abiertas</h1>
        <p className="page-sub">
          §9.5 asigna 3 días posteriores al cierre de campo para codificar C2, C3, N1, N2, N3, P5,
          I6, M2 y M3.
        </p>
      </div>

      {progress.data ? (
        <div className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Avance por pregunta</h2>
          </div>
          <BarChart
            max={1}
            rows={progress.data.map((row) => ({
              key: row.questionCode,
              label: `${row.questionCode} · ${row.questionText.slice(0, 40)}`,
              value: row.share,
              valueLabel: `${fmtInt(row.coded)} / ${fmtInt(row.totalResponses)}`,
              tooltip: `${row.questionCode}: ${fmtInt(row.coded)} codificadas de ${fmtInt(row.totalResponses)} (${fmtPct(row.share, 1)}), ${fmtInt(row.pending)} pendientes`,
            }))}
          />
        </div>
      ) : null}

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="question">
            Pregunta
          </label>
          <select
            id="question"
            className="select"
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              setSelectedCode('');
              setMessage(null);
            }}
          >
            {progress.data?.map((row) => (
              <option key={row.questionCode} value={row.questionCode}>
                {row.questionCode} · {row.pending} pendientes
              </option>
            ))}
          </select>
        </div>
        {activeBook && can('coding:write') ? (
          <>
            <div className="field" style={{ minWidth: 220 }}>
              <label className="field__label" htmlFor="code">
                Código a asignar
              </label>
              <select
                id="code"
                className="select"
                value={selectedCode}
                onChange={(e) => setSelectedCode(e.target.value)}
              >
                <option value="">Elige un código…</option>
                {activeBook.entries.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label}
                    {entry.addedAfterPilot ? ' (post-piloto)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => autoCode.mutate()}
              disabled={autoCode.isPending}
            >
              {autoCode.isPending ? 'Precodificando…' : 'Precodificar exactas'}
            </button>
          </>
        ) : null}
      </div>

      {message ? <div className="callout callout--good">{message}</div> : null}

      {!activeBook ? (
        <div className="callout callout--info">
          <span className="callout__title">Sin libro de códigos</span>
          {question} se codifica a mano porque no tiene un libro cerrado asociado. Las menciones
          espontáneas de personajes (N1–N3) se codifican contra el catálogo de personajes en
          gabinete.
        </div>
      ) : null}

      {pending.data ? (
        pending.data.length === 0 ? (
          <div className="callout callout--good">
            <span className="callout__title">
              <StatusChip status="good">Sin pendientes</StatusChip>
            </span>
            Todas las respuestas de {question} están codificadas.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Respuestas (agrupadas por texto)</th>
                  <th>Ocurrencias</th>
                  {can('coding:write') ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {pending.data.map((group) => (
                  <tr key={group.normalized}>
                    <td>
                      <div className="strong">«{group.samples[0]}»</div>
                      {group.samples.length > 1 ? (
                        <div className="faint">
                          variantes: {group.samples.slice(1).map((s) => `«${s}»`).join(', ')}
                        </div>
                      ) : null}
                    </td>
                    <td className="num strong">{fmtInt(group.occurrences)}</td>
                    {can('coding:write') ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={!selectedCode || codeGroup.isPending}
                          onClick={() =>
                            codeGroup.mutate({
                              answerIds: group.answerIds,
                              code: selectedCode,
                            })
                          }
                        >
                          Asignar código
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="spinner" />
      )}
    </>
  );
}

/* ========================================================================== */
/*                                 Usuarios                                   */
/* ========================================================================== */

export function UsersPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [credential, setCredential] = useState<{ code: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const roles = useQuery({ queryKey: ['users', 'roles'], queryFn: api.roles });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setUserActive(id, isActive),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo cambiar.'),
  });

  /**
   * Edición en la propia fila, sin pantalla aparte.
   *
   * Lo que cambia de un operador en el curso de un levantamiento es el ROL y
   * el SUPERVISOR —alguien sube a supervisor, un encuestador cambia de
   * equipo—, y son dos campos. Un formulario de edición completo obligaría a
   * salir de la lista, que es justo donde se ve a quién hay que mover.
   */
  const updateUser = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: string; supervisorId?: string | null }) =>
      api.updateUser(id, body),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo actualizar.'),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => api.resetPassword(id),
    onSuccess: (result, id) => {
      const user = users.data?.find((u) => u.id === id);
      if (result.temporaryPassword && user) {
        setCredential({ code: user.code, password: result.temporaryPassword });
      }
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Usuarios</h1>
        <p className="page-sub">
          El superadministrador crea las cuentas y entrega las contraseñas en mano. No hay registro
          propio ni recuperación por correo, y el primer acceso obliga a cambiar la contraseña.
        </p>
      </div>

      {/* La contraseña temporal se muestra UNA SOLA VEZ: no queda forma de
          recuperarla después, así que la advertencia es parte del flujo. */}
      {credential ? (
        <div className="callout callout--warning">
          <span className="callout__title">Contraseña temporal · se muestra una sola vez</span>
          <div className="row row--wrap" style={{ marginTop: 8 }}>
            <span>
              Operador <span className="mono strong">{credential.code}</span>
            </span>
            <span className="mono strong" style={{ fontSize: 18 }}>
              {credential.password}
            </span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => {
                void navigator.clipboard.writeText(credential.password);
              }}
            >
              Copiar
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setCredential(null)}
            >
              Ya la anoté
            </button>
          </div>
          <p className="faint" style={{ marginTop: 8, marginBottom: 0 }}>
            Entrégala en persona. Si se pierde, hay que restablecerla: no se puede consultar.
          </p>
        </div>
      ) : null}

      {error ? <div className="callout callout--critical">{error}</div> : null}

      {can('users:write') ? (
        <div className="row" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Cancelar' : 'Crear usuario'}
          </button>
        </div>
      ) : null}

      {showForm && roles.data ? (
        <CreateUserForm
          roles={roles.data}
          supervisors={(users.data ?? []).filter((u) =>
            ['supervisor', 'coordinador', 'superadmin'].includes(u.role),
          )}
          onCreated={(code, password) => {
            setShowForm(false);
            if (password) setCredential({ code, password });
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      ) : null}

      {users.data ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Operador</th>
                <th>Rol</th>
                <th>Supervisor</th>
                <th>Entrevistas</th>
                <th>Último acceso</th>
                <th>Estado</th>
                {can('users:write') ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {users.data.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="strong">{user.name}</div>
                    <div className="faint mono">{user.code}</div>
                    {user.email ? <div className="faint">{user.email}</div> : null}
                  </td>
                  <td>
                    {can('users:write') ? (
                      <select
                        className="input input--sm"
                        value={user.role}
                        disabled={updateUser.isPending}
                        onChange={(e) =>
                          updateUser.mutate({ id: user.id, role: e.target.value })
                        }
                      >
                        {(roles.data ?? []).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="chip chip--accent">{user.roleLabel}</span>
                    )}
                    {user.birthdayRule ? (
                      <div className="faint">
                        cumpleaños: {user.birthdayRule === 'ultimo' ? 'último' : 'próximo'}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {can('users:write') ? (
                      <select
                        className="input input--sm"
                        value={user.supervisor?.id ?? ''}
                        disabled={updateUser.isPending}
                        onChange={(e) =>
                          updateUser.mutate({
                            id: user.id,
                            supervisorId: e.target.value || null,
                          })
                        }
                      >
                        <option value="">Sin supervisor</option>
                        {(users.data ?? [])
                          // Sólo quien puede supervisar, y nunca uno mismo.
                          .filter(
                            (u) =>
                              u.id !== user.id &&
                              u.isActive &&
                              ['supervisor', 'coordinador', 'superadmin'].includes(u.role),
                          )
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <span className="faint">{user.supervisor?.name ?? '—'}</span>
                    )}
                  </td>
                  <td className="num">{fmtInt(user.interviewCount)}</td>
                  <td className="faint">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Nunca'}
                  </td>
                  <td>
                    {user.isActive ? (
                      <StatusChip status="good">Activo</StatusChip>
                    ) : (
                      <StatusChip status="neutral">Inactivo</StatusChip>
                    )}
                    {user.mustChangePassword ? (
                      <div>
                        <StatusChip status="warning">Debe cambiar contraseña</StatusChip>
                      </div>
                    ) : null}
                  </td>
                  {can('users:write') ? (
                    <td>
                      <div className="row row--wrap" style={{ gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() =>
                            setActive.mutate({ id: user.id, isActive: !user.isActive })
                          }
                          disabled={setActive.isPending}
                        >
                          {user.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        {can('users:reset-password') ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => resetPassword.mutate(user.id)}
                            disabled={resetPassword.isPending}
                          >
                            Restablecer
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="spinner" />
      )}

      {roles.data ? (
        <div className="panel" style={{ marginTop: 20 }}>
          <div className="panel__head">
            <h2 className="panel__title">Roles y permisos</h2>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Rol</th>
                  <th>Qué hace</th>
                  <th>Permisos</th>
                </tr>
              </thead>
              <tbody>
                {roles.data.map((role) => (
                  <tr key={role.id}>
                    <td className="strong">{role.label}</td>
                    <td style={{ maxWidth: 420 }} className="faint">
                      {role.description}
                    </td>
                    <td className="num">{role.permissions.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

function CreateUserForm({
  roles,
  supervisors,
  onCreated,
}: {
  roles: { id: string; label: string; description: string }[];
  supervisors: { id: string; code: string; name: string }[];
  onCreated: (code: string, password: string | null) => void;
}) {
  const [form, setForm] = useState({
    code: '',
    name: '',
    email: '',
    role: 'encuestador',
    supervisorId: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createUser({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        role: form.role,
        ...(form.supervisorId ? { supervisorId: form.supervisorId } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      }),
    onSuccess: (result) => onCreated(result.user.code, result.temporaryPassword),
    onError: (e) => {
      const message =
        e instanceof ApiError
          ? (e.fieldErrors ? Object.values(e.fieldErrors).flat().join(' ') : e.message)
          : 'No se pudo crear el usuario.';
      setError(message);
    },
  });

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">Nuevo usuario</h2>
          <p className="panel__note">
            El sistema genera una contraseña temporal legible y la muestra una sola vez, para que la
            entregues en persona. Evita caracteres ambiguos porque se transcribe a mano.
          </p>
        </div>
      </div>

      <div className="grid grid--2">
        <div className="field">
          <label className="field__label" htmlFor="code">
            Código de operador
          </label>
          <input
            id="code"
            className="input"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="ENC01"
            autoCapitalize="characters"
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="name">
            Nombre completo
          </label>
          <input
            id="name"
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="role">
            Rol
          </label>
          <select
            id="role"
            className="select"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.label}
              </option>
            ))}
          </select>
          <p className="faint" style={{ marginTop: 5 }}>
            {roles.find((r) => r.id === form.role)?.description}
          </p>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="supervisor">
            Supervisor de campo
          </label>
          <select
            id="supervisor"
            className="select"
            value={form.supervisorId}
            onChange={(e) => setForm((f) => ({ ...f, supervisorId: e.target.value }))}
          >
            <option value="">Sin asignar</option>
            {supervisors.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="email">
            Correo (opcional)
          </label>
          <input
            id="email"
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="phone">
            Teléfono (opcional)
          </label>
          <input
            id="phone"
            className="input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </div>
      </div>

      {error ? <div className="callout callout--critical">{error}</div> : null}

      <button
        type="button"
        className="btn btn--primary"
        onClick={() => create.mutate()}
        disabled={create.isPending || form.code.trim().length < 2 || form.name.trim().length < 3}
      >
        {create.isPending ? 'Creando…' : 'Crear y generar contraseña'}
      </button>
    </div>
  );
}

/* ========================================================================== */
/*                               Exportación                                  */
/* ========================================================================== */

/**
 * Exportación de la base (§9.5).
 *
 * Los datos de contacto de Q1 y Q2 NUNCA se incluyen, en ninguna variante.
 * §11 los limita al control de calidad.
 */
export function ExportPage() {
  const [includePilot, setIncludePilot] = useState(false);
  const [includeAnnulled, setIncludeAnnulled] = useState(false);
  const [useCodes, setUseCodes] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const codebook = useQuery({ queryKey: ['export', 'codebook'], queryFn: api.codebookExport });

  const download = async (path: string, name: string) => {
    setBusy(name);
    setError(null);
    try {
      await downloadFile(path, name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se pudo descargar.');
    } finally {
      setBusy(null);
    }
  };

  const params = new URLSearchParams({
    ...(includePilot ? { includePilot: 'true' } : {}),
    ...(includeAnnulled ? { includeAnnulled: 'true' } : {}),
    ...(useCodes ? { useCodes: 'true' } : {}),
  });

  const variableCount = Array.isArray(codebook.data?.['variables'])
    ? (codebook.data['variables'] as unknown[]).length
    : 0;
  const derivedCount = Array.isArray(codebook.data?.['derivedVariables'])
    ? (codebook.data['derivedVariables'] as unknown[]).length
    : 0;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Exportación</h1>
        <p className="page-sub">
          §9.5 entrega base de datos limpia (SPSS / CSV), libro de códigos y tabulados. La
          ponderación de §6 se calcula fuera del sistema; la exportación incluye los insumos.
        </p>
      </div>

      <div className="callout callout--info">
        <span className="callout__title">Qué no se exporta</span>
        Q1 (nombre de pila) y Q2 (teléfono) nunca se incluyen, en ninguna variante. §11 los limita al
        control de calidad. Para la verificación telefónica se consultan desde la cola de
        supervisión, y cada consulta queda en la bitácora.
      </div>

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Base de datos</h2>
            <p className="panel__note">
              Formato ancho: una fila por entrevista y una columna por variable, que es lo que
              esperan SPSS y R. Las baterías se despliegan a una columna por fila
              (I1_sarahi_osuna, A_A11, V_V1).
            </p>
          </div>
        </div>

        <div className="stack" style={{ marginBottom: 16 }}>
          <label className="row" style={{ fontSize: 14 }}>
            <input
              type="checkbox"
              checked={includePilot}
              onChange={(e) => setIncludePilot(e.target.checked)}
            />
            Incluir entrevistas del piloto (§8 las levanta fuera de la muestra)
          </label>
          <label className="row" style={{ fontSize: 14 }}>
            <input
              type="checkbox"
              checked={includeAnnulled}
              onChange={(e) => setIncludeAnnulled(e.target.checked)}
            />
            Incluir entrevistas anuladas, marcadas como tales
          </label>
          <label className="row" style={{ fontSize: 14 }}>
            <input
              type="checkbox"
              checked={useCodes}
              onChange={(e) => setUseCodes(e.target.checked)}
            />
            Usar códigos de respuesta en lugar de etiquetas legibles
          </label>
        </div>

        <div className="row row--wrap">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void download(`/export/data.csv?${params}`, 'base.csv')}
            disabled={busy !== null}
          >
            {busy === 'base.csv' ? 'Generando…' : 'Descargar base (CSV)'}
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void download('/export/spss-syntax.sps', 'sintaxis.sps')}
            disabled={busy !== null}
          >
            {busy === 'sintaxis.sps' ? 'Generando…' : 'Sintaxis de SPSS'}
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void download('/export/dispositions.csv', 'disposiciones.csv')}
            disabled={busy !== null}
          >
            {busy === 'disposiciones.csv' ? 'Generando…' : 'Disposiciones (AAPOR)'}
          </button>
        </div>

        {error ? (
          <div className="callout callout--critical" style={{ marginTop: 14, marginBottom: 0 }}>
            {error}
          </div>
        ) : null}

        <details className="note">
          <summary>Por qué sintaxis de SPSS y no un .sav</summary>
          <p>
            Generar la sintaxis en lugar de un archivo binario evita una dependencia externa y deja
            el proceso auditable: el analista ve exactamente cómo se etiquetó cada variable y qué
            valores se marcaron como perdidos. Las opciones no sustantivas (NS/NC, «no tiene
            opinión», «no lo conoce») se declaran como perdidas por el usuario para que no entren en
            las medias.
          </p>
        </details>
      </div>

      {codebook.data ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Libro de códigos <span className="ref">§12</span>
              </h2>
              <p className="panel__note">
                Variables, etiquetas, valores, bases de cálculo y notas metodológicas. §12 lo pide
                como anexo del informe.
              </p>
            </div>
          </div>

          <div className="grid grid--3">
            <Tile label="Variables del cuestionario" value={fmtInt(variableCount)} />
            <Tile label="Variables derivadas" value={fmtInt(derivedCount)} />
            <Tile
              label="Excluidas por privacidad"
              value={
                Array.isArray(codebook.data['excludedForPrivacy'])
                  ? fmtInt((codebook.data['excludedForPrivacy'] as unknown[]).length)
                  : '0'
              }
              meta="Q1 y Q2"
            />
          </div>

          <details className="note">
            <summary>Ver el libro de códigos completo (JSON)</summary>
            <pre
              className="mono"
              style={{
                maxHeight: 420,
                overflow: 'auto',
                background: 'var(--surface-sunken)',
                padding: 12,
                borderRadius: 'var(--radius)',
                fontSize: 12,
              }}
            >
              {JSON.stringify(codebook.data, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </>
  );
}

/* ========================================================================== */
/*                          Bitácora de auditoría                             */
/* ========================================================================== */

/**
 * Bitácora de auditoría.
 *
 * §11 pide conservar la bitácora de campo para auditoría y recomienda que la
 * validación de datos la haga un tercero. Esta pantalla es lo que hace posible
 * esa auditoría: quién hizo qué, cuándo y desde dónde.
 */
export function AuditPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');

  const log = useQuery({
    queryKey: ['audit', action, entityType],
    queryFn: () =>
      api.auditLog({
        take: '200',
        ...(action ? { action } : {}),
        ...(entityType ? { entityType } : {}),
      }),
  });
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Bitácora y configuración</h1>
        <p className="page-sub">
          §11 pide conservar la bitácora para auditoría y recomienda que la validación de datos la
          haga un tercero.
        </p>
      </div>

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="action">
            Acción
          </label>
          <input
            id="action"
            className="input"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="p. ej. pii.viewed"
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="entity">
            Entidad
          </label>
          <select
            id="entity"
            className="select"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="Interview">Entrevista</option>
            <option value="User">Usuario</option>
            <option value="InterviewPii">Datos de contacto</option>
            <option value="SectionEquivalence">Equivalencia de sección</option>
            <option value="CartographyImport">Cartografía</option>
            <option value="Instrument">Instrumento</option>
            <option value="Assignment">Asignación</option>
            <option value="Annulment">Anulación</option>
            <option value="Verification">Verificación</option>
          </select>
        </div>
      </div>

      {log.data ? (
        <div className="table-wrap scroll-y">
          <table className="table">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Quién</th>
                <th>Acción</th>
                <th>Entidad</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {log.data.items.map((entry) => (
                <tr key={entry.id}>
                  <td className="faint">
                    {new Date(entry.createdAt).toLocaleString('es-MX', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </td>
                  <td>
                    {entry.user ? (
                      <>
                        <div className="strong">{entry.user.code}</div>
                        <div className="faint">{entry.user.name}</div>
                      </>
                    ) : (
                      <span className="faint">sistema</span>
                    )}
                    {entry.ipAddress ? <div className="faint mono">{entry.ipAddress}</div> : null}
                  </td>
                  <td className="mono">{entry.action}</td>
                  <td className="faint">
                    {entry.entityType}
                    {entry.entityId ? (
                      <div className="mono faint">{entry.entityId.slice(0, 8)}</div>
                    ) : null}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    {entry.metadata ? (
                      <details className="note">
                        <summary>Ver</summary>
                        <pre
                          className="mono"
                          style={{
                            fontSize: 11,
                            overflow: 'auto',
                            maxHeight: 180,
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(entry.metadata, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="spinner" />
      )}

      {settings.data ? (
        <div className="panel" style={{ marginTop: 20 }}>
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Parámetros del sistema</h2>
              <p className="panel__note">
                Viven en base de datos y no en variables de entorno porque §8 contempla ajustarlos
                tras el piloto, y eso no debe requerir un redespliegue a media jornada de campo.
              </p>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Parámetro</th>
                  <th>Qué controla</th>
                  <th>Referencia</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {settings.data.map((setting) => (
                  <tr key={setting.key}>
                    <td className="mono strong">{setting.key}</td>
                    <td style={{ maxWidth: 360 }}>
                      <div className="strong">{setting.label}</div>
                      <div className="faint">{setting.description}</div>
                    </td>
                    <td>
                      {setting.methodologyReference ? (
                        <span className="ref">{setting.methodologyReference}</span>
                      ) : null}
                    </td>
                    <td>
                      <details className="note">
                        <summary>Ver</summary>
                        <pre className="mono" style={{ fontSize: 11, margin: 0, overflow: 'auto' }}>
                          {JSON.stringify(setting.value, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}
